import { useQuery } from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import { getLocalStorage } from '@/Utils/CommonUtils';

interface GetAvailableCouponsParams {
  tournament_id?: number;
  plan_type?: string;
  duration_months?: number;
  source?: string; // e.g., 'renewal_page', 'checkout_page'
}

interface Coupon {
  code: string;
  discount_type: string;
  discount_value: string;
  currency: string;
  valid_until?: string;
  description: string;
}

interface GetAvailableCouponsResponse {
  coupons: Coupon[];
}

export default function useGetAvailableCoupons(
  params: GetAvailableCouponsParams,
  enabled: boolean = true,
) {
  return useQuery<GetAvailableCouponsResponse, Error>(
    ['available-coupons', params],
    async () => {
      const token = await getLocalStorage('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const queryParams = new URLSearchParams();
      if (params.tournament_id) {
        queryParams.append('tournament_id', params.tournament_id.toString());
      }
      if (params.plan_type) {
        queryParams.append('plan_type', params.plan_type);
      }
      if (params.duration_months) {
        queryParams.append('duration_months', params.duration_months.toString());
      }
      if (params.source) {
        queryParams.append('source', params.source);
      }

      const response = await ChessRestClient.get<GetAvailableCouponsResponse>(
        `subscription/v1/get-available-coupons/?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      );
      return response.data;
    },
    {
      enabled: enabled && !!params.tournament_id && !!params.plan_type, // Only fetch when modal is visible and required params are present
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      refetchOnMount: false, // Don't refetch when component remounts
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  );
}


